import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {flushSync} from 'react-dom';

const editorFlushers=new Set();
export function flushEditorChanges(){editorFlushers.forEach(flush=>flush());}

// Avoid replaying a controlled component's own edits back into its iframe.
// The iframe's synchronous flush is also used before an outside click/submit so
// an unfinished cell is captured before the surrounding exam changes question.
export function useEditorFrame(value,onChange,kind,options) {
    const iframeRef=useRef(null);
    const latest=useRef({value,onChange,options});latest.current={value,onChange,options};
    const lastOptions=useRef();const lastValue=useRef();const lastJSON=useRef();const initialized=useRef(false);
    const publish=useCallback(payload=>{
        const json=JSON.stringify(payload ?? (kind==='editor'?'':null));
        if(initialized.current && json===lastJSON.current)return;
        lastValue.current=payload;lastJSON.current=json;initialized.current=true;
        latest.current.onChange?.(payload);
    },[kind]);
    const send=useCallback((force=false)=>{
        const next=latest.current.value,config=latest.current.options,configJSON=JSON.stringify(config);
        if(!force && initialized.current && configJSON===lastOptions.current && next===lastValue.current)return;
        const json=JSON.stringify(next ?? (kind==='editor'?'':null));
        if(!force && initialized.current && configJSON===lastOptions.current && json===lastJSON.current)return;
        iframeRef.current?.contentWindow?.postMessage({type:'load-data',payload:next,...(config?{options:config}:{})},'*');
        lastOptions.current=configJSON;
        lastValue.current=next;lastJSON.current=json;initialized.current=true;
    },[kind]);
    const onLoad=useCallback(()=>send(true),[send]);
    useEffect(()=>{send();},[value,options,send]);
    useLayoutEffect(()=>{
        const message=event=>{
            if(event.source!==iframeRef.current?.contentWindow)return;
            if(event.data?.type===`${kind}-ready`)send(true);
            if(event.data?.type===`${kind}-change`)publish(event.data.payload);
        };
        const flush=()=>{
            try {
                const payload=iframeRef.current?.contentWindow?.flushEditor?.();
                if(payload!==undefined)publish(JSON.parse(JSON.stringify(payload)));
            } catch { /* The frame may be loading or already removed. */ }
        };
        // Complete state updates before a button or form reads the answer.
        const beforeAction=()=>flushSync(flush);
        editorFlushers.add(flush);
        window.addEventListener('message',message);
        document.addEventListener('pointerdown',flush,true);
        document.addEventListener('click',beforeAction,true);
        document.addEventListener('submit',beforeAction,true);
        window.addEventListener('pagehide',flush);
        return()=>{
            flush();editorFlushers.delete(flush);window.removeEventListener('message',message);
            document.removeEventListener('pointerdown',flush,true);
            document.removeEventListener('click',beforeAction,true);
            document.removeEventListener('submit',beforeAction,true);
            window.removeEventListener('pagehide',flush);
        };
    },[kind,publish,send]);
    // JSON comparison is confined to real changes; selection never serializes
    // the workbook or tells the parent to save it.
    return {iframeRef,onLoad};
}
